import { createHtmlElement, rgbaToHexA, rotation_to_flat } from '../../functions';
import { Buildings, isMobile, isTablet, settingsData } from '../../settings';
import { intersectionClickHandler, selectionBoxesSetStatusColor } from '../threejs/raycast';
import { changeLangToCurrent, isDesktopMode } from '../language/langSwitcher';
import gsap from 'gsap';
import { ViewMode } from '../../common';
import { UnitCardClass } from '../cards/unitCards/UnitCardClass';
import { Box3, default as THREE, MathUtils, MeshBasicMaterial, Vector2, Vector3 } from 'three';
import { getCanvasWidthDiff } from '../../app';
import { CSS2DObject } from '../../libs/CSS2DRenderer';

export class Amenities {

    static parentContainer;
    static amenitiesContainer;
    static amenitiesArray = [];

    constructor (building) {
        this.amenities = building.customSelections;
    }

    add(){
        if (!this.amenities.length) return;

        Amenities.clear();

        this.amenities.forEach(amenity =>{

            const amenityElement = new Amenity(Amenities.amenitiesContainer).create(amenity);
            Amenities.amenitiesArray.push(amenityElement);

        });

        Amenities.show();

    }

    static createMainContainer(){
        // const checkAmenities = buildings_info.some(building => building.customSelections.length);
        const checkAmenities = Buildings.getBuildingsArray().some(building => building.customSelections.length);
        if (!checkAmenities) return;

        // this.parentContainer = isDesktopMode() || isMobile ? document.body : document.querySelector('.right-nav-bar__wrapper');
        this.parentContainer = document.body;

        if (!this.exist()){
            this.amenitiesContainer = createHtmlElement(this.parentContainer, '', 'div', ['amenities__main__container','non-canvas']);
        } else {
            this.amenitiesContainer = this.exist();
        }
    }

    static hide () {
        if (!this.exist()) return;

        if (this.amenitiesContainer.classList.contains('hidden')) return;

        gsap.to(this.amenitiesContainer,{
            scale:0,
            duration: .3,
            onComplete: () =>{
                this.amenitiesContainer.classList.add('hidden');
                this.amenitiesContainer.style.removeProperty('transform');
            }
        });
    }

    static show (delay = .8) {
       if (!this.exist()) return;

       if (UnitCardClass.findCard()?.html){
           return;
       }

       gsap.fromTo(this.amenitiesContainer,{
           scale: 0
       },{
           scale: 1,
           delay: delay,
           duration: .5,
           onStart: () => this.amenitiesContainer.classList.remove('hidden'),
           onComplete: () =>{
               this.amenitiesContainer.style.removeProperty('transform');
           }
       });
    }

    static clear(){
        if (!this.exist()) return;

        while (this.amenitiesContainer.firstElementChild){
            this.amenitiesContainer.firstElementChild.remove();
        }

        this.amenitiesArray = [];
    }

    static unselectAmenities(){
        this.exist() && this.amenitiesArray.forEach(amenity => amenity.hoverOut());
    }

    static exist(){
        return this.amenitiesContainer || document.querySelector('.amenities__main__container');
    }

}


class Amenity {

    constructor (container) {
        this.container = container;
        this.amenity = null;
        this.element = null;
        this.crmData = null;
        this.highlightColor = rgbaToHexA(config.style.colors.find(color => color.key === 'brand2').value.split(','),false).substring(1);
        this.active = false;
        this.name = null;
        this.amenityPoint = createHtmlElement('','','div',['amenity__mesh__point','hide__point']);
        const span = createHtmlElement(this.amenityPoint,'','span',['amenity__mesh__span']);
        this.amenityLabel = new CSS2DObject(this.amenityPoint);
        this.amenityLabel.userData.zoom = 1;
        this.amenityLabel.name = `amenity`;
        this.line = null;

    }

    create(amenity) {
        this.amenity = amenity;
        this.amenity.add(this.amenityLabel);

        this.crmData = amenity.userData.crm_data;
        const parseName = this.crmData.propNum.match(/([A-Z]|([a-z]))/g).join('').toLowerCase();
        this.name = parseName.charAt(0).toUpperCase() + parseName.substring(1);
        const imgUrl = this.crmData.assets.preview2D;

        this.element = createHtmlElement(this.container, '', 'div', ['amenity__btn']);
        this.element.setAttribute('amenity', this.name);
        this.element.style.backgroundImage = `url(${imgUrl})`;

        if (isMobile || isDesktopMode() || isTablet) {
            this.element.addEventListener('click', () => {
                if (isMobile || isTablet){
                    this.click();
                } else {
                    if (this.element.classList.contains('active__amenity')) {
                        this.hoverOut();
                    } else {
                        this.hover();
                    }
                }
            });
        } else {
            this.element.addEventListener('mouseenter', (e) => this.hover(e));
            this.element.addEventListener('mouseleave', () => this.hoverOut());
            this.element.addEventListener('click', () => this.click());
        }

        const text = createHtmlElement(this.element, changeLangToCurrent(this.name), 'span', ['amenity__text', 'language-string']);
        text.setAttribute('data-dictionary', this.name);

        return this;
    }

    hover(){

        this.element.classList.add('active__amenity');
        this.active = true;
        rotation_to_flat(this.amenity);
        selectionBoxesSetStatusColor(this.amenity,'amenityHover',false,this.highlightColor);

        if (!isMobile){
            this.drawLine();
            this.updateLine();
        }

    }

    drawLine(){
        if (this.line) return;

        this.line = createHtmlElement(this.element,'','div',['amenity__line']);
    }

    updateLine(){

        if (this.active){
            requestAnimationFrame(this.updateLine.bind(this));

            const {bottom,left} = this.element.getBoundingClientRect();
            const amenity2D = this.amenityPoint.getBoundingClientRect();
            const distanceX = Math.abs(left - amenity2D.left);
            const distanceY = Math.abs(bottom - amenity2D.bottom);
            const square = Math.sqrt((distanceX * distanceX) + (distanceY * distanceY));
            const angle = Math.atan2(amenity2D.bottom - bottom,amenity2D.left - left);
            const degree = MathUtils.radToDeg(angle);

            this.line.style.transform = `rotateZ(${degree}deg)`;
            this.line.style.width = `${square}px`;
        }
    }

    hoverOut(){
        this.active = false;
        this.element.classList.remove('active__amenity');
        selectionBoxesSetStatusColor(this.amenity,'hoverOut',false);

        if (this.line){
            this.line.remove();
            this.line = null;
        }
    }

    click(){

        intersectionClickHandler(this.amenity);

    }

}